import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import MuiSnackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Schedule as ScheduleIcon,
  Image as ImageIcon,
  Send as SendIcon,
  Star as AIIcon,
} from '@mui/icons-material';
import socialService, { SocialPost, Platform } from '../../api/services/social';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`social-tabpanel-${index}`}
      aria-labelledby={`social-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const SocialMedia: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [openPostDialog, setOpenPostDialog] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [scheduledPosts, setScheduledPosts] = useState<SocialPost[]>([]);
  const [recentPosts, setRecentPosts] = useState<SocialPost[]>([]);
  const [generatingContent, setGeneratingContent] = useState(false);

  // Mock business ID for now - should come from auth context
  const businessId = 1;

  const loadPlatforms = useCallback(async () => {
    try {
      const platformsData = await socialService.getPlatforms(businessId);
      setPlatforms(platformsData.map(p => ({
        ...p,
        icon: getPlatformIcon(p.name),
        color: getPlatformColor(p.name),
      })));
    } catch (err) {
      setError('Failed to load platforms');
      console.error(err);
    }
  }, [businessId]);

  const loadPosts = useCallback(async () => {
    try {
      const [scheduled, recent] = await Promise.all([
        socialService.getPosts(businessId, { status: 'scheduled' }),
        socialService.getPosts(businessId, { status: 'published' }),
      ]);
      setScheduledPosts(scheduled);
      setRecentPosts(recent);
    } catch (err) {
      setError('Failed to load posts');
      console.error(err);
    }
  }, [businessId]);

  useEffect(() => {
    loadPlatforms();
    loadPosts();
  }, [loadPlatforms, loadPosts]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenPostDialog = () => {
    setOpenPostDialog(true);
  };

  const handleClosePostDialog = () => {
    setOpenPostDialog(false);
    setPostContent('');
    setSelectedPlatforms([]);
  };

  const handlePlatformToggle = (platform: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const handleGenerateContent = async () => {
    setGeneratingContent(true);
    try {
      const { content } = await socialService.generateContent(businessId, {
        platforms: selectedPlatforms,
        type: 'post',
        tone: 'professional',
      });
      setPostContent(content);
      setSuccess('Content generated successfully');
    } catch (err) {
      setError('Failed to generate content');
      console.error(err);
    } finally {
      setGeneratingContent(false);
    }
  };

  const handlePost = async () => {
    setLoading(true);
    try {
      await socialService.createPost({
        business_id: businessId,
        content: postContent,
        platforms: selectedPlatforms,
      });
      setSuccess('Post created successfully');
      handleClosePostDialog();
      loadPosts(); // Refresh posts
    } catch (err) {
      setError('Failed to create post');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSchedulePost = async () => {
    setLoading(true);
    try {
      await socialService.createPost({
        business_id: businessId,
        content: postContent,
        platforms: selectedPlatforms,
        scheduled_for: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Schedule for tomorrow
      });
      setSuccess('Post scheduled successfully');
      handleClosePostDialog();
      loadPosts(); // Refresh posts
    } catch (err) {
      setError('Failed to schedule post');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (name: string) => {
    switch (name.toLowerCase()) {
      case 'facebook': return <FacebookIcon />;
      case 'twitter': return <TwitterIcon />;
      case 'instagram': return <InstagramIcon />;
      case 'linkedin': return <LinkedInIcon />;
      default: return null;
    }
  };

  const getPlatformColor = (name: string) => {
    switch (name.toLowerCase()) {
      case 'facebook': return '#1877F2';
      case 'twitter': return '#1DA1F2';
      case 'instagram': return '#E4405F';
      case 'linkedin': return '#0A66C2';
      default: return '#757575';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Social Media Management</Typography>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleOpenPostDialog}
        >
          Create Post
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="social media tabs"
          centered
        >
          <Tab label="Overview" />
          <Tab label="Scheduled Posts" />
          <Tab label="Recent Posts" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {platforms.map((platform) => (
              <Grid item xs={12} sm={6} md={3} key={platform.name}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: platform.color, mr: 1 }}>
                        {platform.icon}
                      </Avatar>
                      <Typography variant="h6">{platform.name}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Last 7 days
                    </Typography>
                    <Typography variant="h4" component="div">
                      {Math.floor(Math.random() * 1000)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Engagement
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            {scheduledPosts.map((post) => (
              <Grid item xs={12} key={post.id}>
                <Paper sx={{ p: 2 }} role="article">
                  <Box display="flex" alignItems="center" mb={1}>
                    <ScheduleIcon sx={{ mr: 1 }} color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {new Date(post.scheduled_for!).toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography variant="body1" gutterBottom>
                    {post.content}
                  </Typography>
                  <Box display="flex" gap={1}>
                    {post.platforms.map((platform) => (
                      <Chip
                        key={platform}
                        label={platform}
                        size="small"
                        icon={getPlatformIcon(platform)}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={2}>
            {recentPosts.map((post) => (
              <Grid item xs={12} key={post.id}>
                <Paper sx={{ p: 2 }} role="article">
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ mr: 1, bgcolor: getPlatformColor(post.platforms[0]) }}>
                        {getPlatformIcon(post.platforms[0])}
                      </Avatar>
                      <Typography variant="subtitle1">{post.platforms.join(', ')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(post.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography variant="body1" gutterBottom>
                    {post.content}
                  </Typography>
                  {post.engagement && (
                    <Box display="flex" gap={2}>
                      <Typography variant="body2">
                        {post.engagement.likes} Likes
                      </Typography>
                      <Typography variant="body2">
                        {post.engagement.comments} Comments
                      </Typography>
                      <Typography variant="body2">
                        {post.engagement.shares} Shares
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </Paper>

      {/* Create Post Dialog */}
      <Dialog open={openPostDialog} onClose={handleClosePostDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Post</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Post Content"
            aria-label="Post Content"
            fullWidth
            multiline
            rows={4}
            value={postContent}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPostContent(e.target.value)}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select Platforms
            </Typography>
            <Box display="flex" gap={1}>
              {platforms.map((platform) => (
                <Chip
                  key={platform.name}
                  label={platform.name}
                  icon={platform.icon}
                  onClick={() => handlePlatformToggle(platform.name)}
                  color={selectedPlatforms.includes(platform.name) ? "primary" : "default"}
                  clickable
                />
              ))}
            </Box>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              fullWidth
            >
              Add Media
            </Button>
            <Button
              variant="outlined"
              startIcon={<AIIcon />}
              onClick={handleGenerateContent}
              disabled={generatingContent || selectedPlatforms.length === 0}
              fullWidth
            >
              Generate with AI
            </Button>
          </Box>
          {generatingContent && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePostDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePost}
            disabled={!postContent || selectedPlatforms.length === 0 || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Post'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ScheduleIcon />}
            onClick={handleSchedulePost}
            disabled={!postContent || selectedPlatforms.length === 0 || loading}
          >
            Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error/Success Snackbars */}
      <MuiSnackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <MuiAlert severity="error" onClose={() => setError(null)}>
          {error}
        </MuiAlert>
      </MuiSnackbar>

      <MuiSnackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <MuiAlert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </MuiAlert>
      </MuiSnackbar>
    </Box>
  );
};

export default SocialMedia;
